import { isE2eFixtureTenant } from '@lib/e2eFixtureTenant'
import { useTenants } from '@lib/tenantsContext'
import { Checkbox, Group, Stack } from '@mantine/core'
import { usePersistentState } from '@packages/core'
import { FilterInput, H1, Button, SortableTable } from '@packages/ui'
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconLock,
  IconRefresh
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

export const Dashboard = () => {
  const { t } = useTranslation()
  const tenants = useTenants()
  const navigate = useNavigate()
  const [filterFor, setFilterFor] = useState<string>('')
  const [filterBy, setFilterBy] = usePersistentState<
    'all' | 'new' | 'active' | 'expired' | 'blocked' | boolean
  >('admin-filter-tenant-by', 'new')
  const [hideE2eTenants, setHideE2eTenants] = usePersistentState(
    'admin-hide-e2e-tenants',
    true
  )

  const data = useMemo(() => {
    if (!tenants.data) {
      return []
    }
    let rows = tenants.data
    if (hideE2eTenants) {
      rows = rows.filter((tenant) => !isE2eFixtureTenant(tenant))
    }
    if (!filterBy || filterBy === 'all') return rows

    switch (filterBy) {
      case 'active':
        return rows.filter(
          (e: Tenant) =>
            !e.demo_expiry_date || dayjs(e.demo_expiry_date).isAfter()
        )
      case 'expired':
        return rows.filter((e: Tenant) => dayjs(e.demo_expiry_date).isBefore())
      case 'blocked':
        return rows.filter((e: Tenant) => e.suspended_at !== null)
      case 'new':
        return rows.filter((e) =>
          dayjs(e.created_at).isAfter(dayjs().subtract(5, 'days'))
        )
    }
  }, [filterBy, hideE2eTenants, tenants.data])

  const selectTenant = (tenant: Tenant) => {
    navigate(`/tenants/${tenant.id}`, { state: { tenant } })
  }

  return (
    <Stack c={'white'}>
      <H1 className={'text-white'}>Dashboard</H1>

      <Group justify={'space-between'}>
        <Group c={'white'}>
          <Button
            variant="transparent"
            active={filterBy === 'new'}
            onClick={() => setFilterBy('new')}
          >
            New
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'active'}
            onClick={() => setFilterBy('active')}
          >
            Active
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'expired'}
            onClick={() => setFilterBy('expired')}
          >
            Expired
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'blocked'}
            onClick={() => setFilterBy('blocked')}
          >
            Blocked
          </Button>

          <Button
            variant="transparent"
            active={!filterBy || filterBy === 'all'}
            onClick={() => setFilterBy('all')}
          >
            All
          </Button>

          <Checkbox
            classNames={{ label: 'text-white' }}
            checked={hideE2eTenants}
            onChange={(e) => setHideE2eTenants(e.currentTarget.checked)}
            label={t('admin.tenants.hide_e2e_fixtures', {
              defaultValue: 'Hide E2E test tenants'
            })}
          />

          <Button
            loading={tenants.isLoading || tenants.isValidating}
            onClick={() => tenants.mutate()}
          >
            <IconRefresh size={20} />
          </Button>
        </Group>
        <Group>
          <FilterInput onFilter={setFilterFor} filter={filterFor} />
        </Group>
      </Group>

      <Stack>
        <SortableTable
          filterBy={filterFor}
          data={data}
          columns={[
            {
              key: 'name',
              label: 'Name'
            },
            {
              key: 'suspended_at',
              label: 'Status',
              render: (_e, tenant) => {
                return (
                  <Group>
                    {tenant.suspended_at && (
                      <Group>
                        <IconLock color={'red'} /> Blocked
                      </Group>
                    )}
                    {tenant.demo_expiry_date && (
                      <>
                        {dayjs(tenant.demo_expiry_date).isBefore(
                          new Date()
                        ) && (
                          <Group>
                            <IconAlertCircle color={'red'} /> Trial Expired
                          </Group>
                        )}
                        {dayjs(tenant.demo_expiry_date).isAfter(
                          dayjs().subtract(5, 'days')
                        ) && (
                          <Group>
                            <IconAlertTriangle color={'orange'} /> Trial ending
                          </Group>
                        )}
                      </>
                    )}
                  </Group>
                )
              }
            },
            { key: 'inactive_users', label: 'Inactive Users', render: () => 0 },
            {
              key: 'active_users',
              label: 'Active Users',
              render: (_v, tenant) => tenant.user_count[0].count
            },
            {
              key: 'created_at',
              label: 'Registered',
              render: (created_at) => dayjs(created_at).format('DD.MM.YYYY')
            }
          ]}
          rowOnClick={selectTenant}
        />
      </Stack>
    </Stack>
  )
}
